/**
 * VireoHR - Employee Home Screen
 * Ultra-Minimal Design with Clock In/Out and Leave Request Modal
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { firestore } from '@/lib/firebase/client';
import { recordAttendance, clockOut, createLeaveRequest } from '@/services/cloudFunctions';
import { Schedule, Attendance } from '@/types/models';

// Partial attendance for UI state (only what's needed for display)
interface AttendanceDisplay {
  id: string;
  scheduleId?: string;
  status: string;
  clockInTimestamp: string;
  clockOutTimestamp?: string;
}
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import toast from 'react-hot-toast';

export default function EmployeeHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentAttendance, setCurrentAttendance] = useState<AttendanceDisplay | null>(null);
  const [currentSchedule, setCurrentSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  
  // Loading states for clock-in/out operations
  const [isClockingIn, setIsClockingIn] = useState(false);
  const [isClockingOut, setIsClockingOut] = useState(false);
  
  // Leave request form
  const [leaveDate, setLeaveDate] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveLoading, setLeaveLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchTodayData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Fixed: Added user?.id to dependency array

  const fetchTodayData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const schedulesRef = collection(firestore, 'schedules');
      const scheduleQuery = query(
        schedulesRef,
        where('employeeIds', 'array-contains', user.id),
        where('startTimestamp', '>=', today.toISOString()),
        where('startTimestamp', '<', tomorrow.toISOString()),
        limit(1)
      );

      const scheduleSnapshot = await getDocs(scheduleQuery);
      if (!scheduleSnapshot.empty) {
        const scheduleData = scheduleSnapshot.docs[0].data() as Omit<Schedule, 'id'>;
        setCurrentSchedule({ ...scheduleData, id: scheduleSnapshot.docs[0].id });

        // Check if already clocked in
        const attendanceRef = collection(firestore, 'attendance');
        const attendanceQuery = query(
          attendanceRef,
          where('employeeId', '==', user.id),
          where('scheduleId', '==', scheduleSnapshot.docs[0].id),
          limit(1)
        );

        const attendanceSnapshot = await getDocs(attendanceQuery);
        if (!attendanceSnapshot.empty) {
          const attData = attendanceSnapshot.docs[0].data() as Omit<Attendance, 'id'>;
          setCurrentAttendance({ ...attData, id: attendanceSnapshot.docs[0].id });
        }
      }
    } catch (err: any) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    if (!user?.id || !user?.tenantId || !currentSchedule || isClockingIn) return;

    setIsClockingIn(true);

    try {
      // Get geolocation
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: true,
        });
      });

      // Generate device ID (browser fingerprint)
      const deviceId = navigator.userAgent.substring(0, 50);

      const result = await recordAttendance({
        tenantId: user.tenantId,
        scheduleId: currentSchedule.id,
        branchId: currentSchedule.branchId || currentSchedule.storeId || '',
        coords: {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        },
        deviceId: deviceId,
        clientTimestamp: Date.now(),
      });

      // Handle new idempotent responses
      if (result.data.status === 'ALREADY_CLOCKED_IN') {
        toast('Already clocked in.', { icon: 'ℹ️' });
        // Update UI with existing attendance
        setCurrentAttendance({
          id: result.data.attendance.id,
          scheduleId: result.data.attendance.scheduleId,
          status: result.data.attendance.status,
          clockInTimestamp: result.data.attendance.clockInTimestamp,
          clockOutTimestamp: result.data.attendance.clockOutTimestamp || undefined,
        });
      } else if (result.data.status === 'CLOCKED_IN') {
        toast.success(result.data.message || 'Clocked in successfully');
        fetchTodayData();
      }
    } catch (error: any) {
      console.error('Clock-in error:', error);
      toast.error(error.message || 'Failed to clock in');
    } finally {
      setIsClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    if (!user?.tenantId || isClockingOut) return;

    setIsClockingOut(true);

    try {
      // Get geolocation
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: true,
        });
      });

      // Generate device ID (browser fingerprint)
      const deviceId = navigator.userAgent.substring(0, 50);

      const result = await clockOut({
        tenantId: user.tenantId,
        coords: {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        },
        deviceId: deviceId,
        clientTimestamp: Date.now(),
      });

      // Handle new idempotent responses
      if (result.data.status === 'NO_ACTIVE_SHIFT') {
        toast('You are already clocked out.', { icon: 'ℹ️' });
        // Clear current attendance from UI
        setCurrentAttendance(null);
      } else if (result.data.status === 'ALREADY_CLOCKED_OUT') {
        toast('Attendance already clocked out.', { icon: 'ℹ️' });
        // Clear current attendance from UI
        setCurrentAttendance(null);
      } else if (result.data.status === 'CLOCKED_OUT') {
        toast.success(result.data.message || 'Clocked out successfully');
        fetchTodayData();
      }
    } catch (error: any) {
      console.error('Clock-out error:', error);
      toast.error(error.message || 'Failed to clock out');
    } finally {
      setIsClockingOut(false);
    }
  };

  const handleLeaveRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.id || !user?.tenantId) return;

    setLeaveLoading(true);

    try {
      const result = await createLeaveRequest({
        tenantId: user.tenantId,
        employeeId: user.id,
        employeeName: user.name,
        date: leaveDate,
        reason: leaveReason,
      });

      if (result.data.success) {
        toast.success('Leave request submitted');
        setShowLeaveModal(false);
        setLeaveDate('');
        setLeaveReason('');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit leave request');
    } finally {
      setLeaveLoading(false);
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isClockedIn = currentAttendance && !currentAttendance.clockOutTimestamp;

  if (loading) {
    return (
      <Layout title="Home">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Home">
      <div className="p-4 space-y-4">
        {/* Greeting */}
        <div>
          <h2 className="text-lg font-semibold text-[#0D9488]">
            Hello, {user?.name || 'Employee'}!
          </h2>
          <p className="text-xs text-[#D4AF37] mt-0.5">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>

        {/* Clock In/Out */}
        <Card>
          <div className="text-center py-6">
            <p className="text-sm text-[#D4AF37] mb-4">
              {isClockedIn ? 'Currently Working' : 'Ready to Start'}
            </p>

            {!isClockedIn ? (
              <button
                onClick={handleClockIn}
                disabled={!currentSchedule || isClockingIn}
                className="w-32 h-32 rounded-full bg-[#0D9488] text-white text-base font-semibold hover:bg-[#0a7d73] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isClockingIn ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                ) : (
                  'Clock In'
                )}
              </button>
            ) : (
              <button
                onClick={handleClockOut}
                disabled={isClockingOut}
                className="w-32 h-32 rounded-full bg-red-600 text-white text-base font-semibold hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isClockingOut ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                ) : (
                  'Clock Out'
                )}
              </button>
            )}

            {isClockedIn && currentAttendance && (
              <div className="mt-4">
                <p className="text-xs text-[#D4AF37]">Clocked in at</p>
                <p className="text-sm font-medium text-[#0D9488] mt-0.5">
                  {formatTime(currentAttendance.clockInTimestamp)}
                </p>
                {currentAttendance.status === 'LATE' && (
                  <Badge variant="warning" className="mt-2">Late</Badge>
                )}
              </div>
            )}

            {!currentSchedule && !isClockedIn && (
              <p className="text-xs text-[#D4AF37] mt-4">No shift scheduled today</p>
            )}
          </div>
        </Card>

        {/* Today's Schedule */}
        {currentSchedule && (
          <Card>
            <h3 className="text-sm font-semibold text-[#0D9488] mb-3">Today's Shift</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-[#D4AF37]">
                <span className="text-sm text-[#D4AF37]">Time</span>
                <span className="text-sm font-medium text-[#0D9488]">
                  {formatTime(currentSchedule.startTimestamp)} - {formatTime(currentSchedule.endTimestamp)}
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button onClick={() => setShowLeaveModal(true)} variant="outline">
            Request Leave
          </Button>
          <Button onClick={() => navigate('/employee/schedule')} variant="outline">
            View Schedule
          </Button>
        </div>
      </div>

      {/* Leave Request Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-sm w-full">
            <h2 className="text-base font-semibold text-[#0D9488] mb-4">Request Leave</h2>
            
            <form onSubmit={handleLeaveRequest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#D4AF37] mb-1">Date *</label>
                <input
                  type="date"
                  value={leaveDate}
                  onChange={(e) => setLeaveDate(e.target.value)}
                  className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#D4AF37] mb-1">Reason *</label>
                <textarea
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={leaveLoading} className="flex-1" variant="primary">
                  {leaveLoading ? 'Submitting...' : 'Submit'}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowLeaveModal(false);
                    setLeaveDate('');
                    setLeaveReason('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </Layout>
  );
}
